import { isMemberSubmittableStep, type VerificationStepKey } from '@aysar/contracts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/shared/hooks/useToast/useToast';
import { type ModalData } from '@/shared/store/reducers/modal/modal.types';
import { toApiError } from '@/shared/utils/transform-error/transform-error';

import {
  useCaseDetailQuery,
  useRequestChangesMutation,
} from '../../services/verification/verification.api';
import { verificationErrorCopy } from '../../utils/verification-error/verification-error.util';

export interface UseRequestChangesModalLogic {
  caseId: string | null;
  member: string | null;
  /** The steps this dialog may offer — member-submittable and not yet closed. */
  available: readonly VerificationStepKey[];
  selected: readonly VerificationStepKey[];
  toggleStep: (step: VerificationStepKey) => void;
  note: string;
  setNote: (value: string) => void;
  /** Every precondition the api enforces, mirrored so the button is honest. */
  canSubmit: boolean;
  isSending: boolean;
  error: string | null;
  submit: () => Promise<void>;
}

/**
 * Sending named steps back to the member.
 *
 * ## The dialog offers exactly what the contract accepts
 *
 * `RequestChangesSchema` refuses a step that is not member-submittable —
 * *"asking somebody to redo the automated face match is an instruction they
 * cannot follow"* — and requires at least one, with no duplicates, plus a
 * non-empty note. Every one of those is mirrored here, so the submit button is a
 * statement about what will happen rather than an invitation to a 422.
 *
 * A step a reviewer already **passed** or **waived** is closed and is not
 * offered either: `submitStep` refuses to overwrite a resolved step, so asking
 * for it would send the member to a control their own app will refuse. A
 * `failed` one **is** offered — that is precisely the request to send it again.
 *
 * ## It reads the case from the cache, not from the payload
 *
 * `ModalData` is serializable-only, so the payload carries the case id and
 * nothing else worth trusting. The steps come from the same `caseDetail` query
 * the screen behind the dialog is already subscribed to — one cache entry, so
 * the dialog cannot be looking at an older case than the page under it.
 */
export function useRequestChangesModalLogic(
  modalData: ModalData | undefined,
  onClose: () => void,
): UseRequestChangesModalLogic {
  const { t } = useTranslation('verification');
  const toast = useToast();
  const [selected, setSelected] = useState<readonly VerificationStepKey[]>([]);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const caseId = readString(modalData?.['caseId']);
  const query = useCaseDetailQuery(caseId ?? '', { skip: caseId === null });
  const [requestChanges, { isLoading: isSending }] = useRequestChangesMutation();

  const available = useMemo<readonly VerificationStepKey[]>(
    () =>
      (query.data?.steps ?? [])
        .filter(
          (step) =>
            isMemberSubmittableStep(step.key) &&
            step.status !== 'passed' &&
            step.status !== 'waived',
        )
        .map((step) => step.key),
    [query.data],
  );

  const toggleStep = useCallback((step: VerificationStepKey) => {
    setSelected((current) =>
      current.includes(step) ? current.filter((key) => key !== step) : [...current, step],
    );
  }, []);

  const submit = useCallback(async () => {
    if (caseId === null || selected.length === 0 || note.trim() === '') return;

    setError(null);

    try {
      await requestChanges({ id: caseId, steps: [...selected], note: note.trim() }).unwrap();
      toast.success(t('requestChanges.success'));
      onClose();
    } catch (caught) {
      const copy = verificationErrorCopy(toApiError(caught));
      setError(copy === null ? t('error.generic') : t(copy.i18nKey));
    }
  }, [caseId, note, onClose, requestChanges, selected, t, toast]);

  return {
    caseId,
    member: query.data?.account?.email ?? null,
    available,
    // Never offer a step the case does not have any more: the query can refresh
    // under an open dialog, and a selection made before that must not survive it.
    selected: selected.filter((step) => available.includes(step)),
    toggleStep,
    note,
    setNote,
    canSubmit:
      caseId !== null &&
      selected.some((step) => available.includes(step)) &&
      note.trim() !== '' &&
      !isSending,
    isSending,
    error,
    submit,
  };
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null;
  return value;
}
